import React, { useState, useEffect } from 'react';
import { ingestionService } from '../ingestionService';
import { IngestionReportFormQuestion } from '../types';

interface QuestionEditorModalProps {
  question: IngestionReportFormQuestion | any | null;
  formTitle?: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: (updated: any) => void;
}

const AVIATION_FLIGHT_PHASE_PRESET = [
  'Standing / Parked',
  'Pushback / Towing',
  'Taxi',
  'Takeoff',
  'Initial Climb',
  'En Route / Cruise',
  'Descent',
  'Approach',
  'Landing',
  'Post-Flight / Shutdown',
  'Other',
];

export const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({
  question,
  formTitle,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [label, setLabel] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [questionType, setQuestionType] = useState('TEXT');
  const [isRequired, setIsRequired] = useState(false);
  const [helpText, setHelpText] = useState('');
  const [options, setOptions] = useState<string[]>([]);
  const [newOption, setNewOption] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (question && isOpen) {
      setLabel(question.label || '');
      setSectionName(question.section_name || 'General Details');
      setQuestionType(question.question_type || 'TEXT');
      setIsRequired(Boolean(question.is_required));
      setHelpText(question.help_text || '');
      setErrorMsg(null);
      setNewOption('');

      let parsed: string[] = [];
      if (question.options_json) {
        try {
          parsed = JSON.parse(question.options_json);
        } catch {
          parsed = [];
        }
      }
      if (parsed.length === 0 && (question.question_key === 'flight_phase' || question.question_type === 'SELECT')) {
        parsed = [...AVIATION_FLIGHT_PHASE_PRESET];
      }
      setOptions(parsed);
    }
  }, [question, isOpen]);

  if (!isOpen || !question) return null;

  const handleAddOption = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = newOption.trim();
    if (!trimmed) return;
    if (options.includes(trimmed)) {
      setErrorMsg(`"${trimmed}" is already in the options list.`);
      return;
    }
    setErrorMsg(null);
    setOptions([...options, trimmed]);
    setNewOption('');
  };

  const handleRemoveOption = (index: number) => {
    const updated = options.filter((_, i) => i !== index);
    setOptions(updated);
  };

  const handleMoveOption = (index: number, direction: 'UP' | 'DOWN') => {
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= options.length) return;
    const copy = [...options];
    const temp = copy[index];
    copy[index] = copy[targetIndex];
    copy[targetIndex] = temp;
    setOptions(copy);
  };

  const handleToggleOther = () => {
    if (options.some((o) => o.toLowerCase() === 'other' || o.toLowerCase().startsWith('other'))) {
      setOptions(options.filter((o) => !(o.toLowerCase() === 'other' || o.toLowerCase().startsWith('other'))));
    } else {
      setOptions([...options, 'Other']);
    }
  };

  const handleLoadFlightPhasePreset = () => {
    setQuestionType('SELECT');
    setOptions([...AVIATION_FLIGHT_PHASE_PRESET]);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      setErrorMsg('Question label cannot be empty.');
      return;
    }

    try {
      setSaving(true);
      setErrorMsg(null);

      const payload: any = {
        label: label.trim(),
        section_name: sectionName.trim() || 'General Details',
        question_type: questionType,
        is_required: isRequired,
        help_text: helpText.trim() || undefined,
        options: questionType === 'SELECT' || questionType === 'MULTI_SELECT' ? options : undefined,
      };

      const updated = await ingestionService.updateQuestion(question.id, payload);
      onSaved(updated);
      onClose();
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to save question changes.');
    } finally {
      setSaving(false);
    }
  };

  const hasOther = options.some((o) => o.toLowerCase() === 'other' || o.toLowerCase().startsWith('other'));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '580px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#6366f1',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              ⚙️ Admin Configuration • Question Editor
            </span>
            <h3 style={{ margin: '2px 0 0 0', fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
              {formTitle ? `${formTitle} — ${question.question_key}` : question.question_key}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} style={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
          {errorMsg && (
            <div
              style={{
                padding: '10px 14px',
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                color: '#991b1b',
                fontSize: '12px',
                marginBottom: '14px',
              }}
            >
              ⚠️ {errorMsg}
            </div>
          )}

          {/* Question Label */}
          <div className="form-field" style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
              Question Label (Displayed to Reporter) *
            </label>
            <input
              type="text"
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Flight Phase (Taxi, Takeoff, Cruise, Landing)"
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
            />
          </div>

          {/* Row: Type & Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                Question Input Type *
              </label>
              <select
                value={questionType}
                onChange={(e) => setQuestionType(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              >
                <option value="SELECT">Dropdown Menu (Single Select)</option>
                <option value="TEXT">Short Text Field</option>
                <option value="TEXTAREA">Multi-line Paragraph</option>
                <option value="MULTI_SELECT">Multi-Select List</option>
                <option value="BOOLEAN">Yes / No Toggle</option>
                <option value="NUMBER">Numeric Value</option>
                <option value="DATE">Date Picker</option>
              </select>
            </div>

            <div className="form-field" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                Form Section Group
              </label>
              <input
                type="text"
                value={sectionName}
                onChange={(e) => setSectionName(e.target.value)}
                placeholder="Flight Data / Event Details"
                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
              />
            </div>
          </div>

          {/* If SELECT: Dropdown Options Management */}
          {(questionType === 'SELECT' || questionType === 'MULTI_SELECT') && (
            <div
              style={{
                marginBottom: '16px',
                padding: '14px',
                background: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>
                  📋 Dropdown Choices ({options.length} items)
                </span>
                {question.question_key === 'flight_phase' && (
                  <button
                    type="button"
                    onClick={handleLoadFlightPhasePreset}
                    style={{
                      background: '#e0e7ff',
                      color: '#4338ca',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    ⚡ Load Standard Flight Phases
                  </button>
                )}
              </div>

              {/* Add New Option Input */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Type new dropdown choice and press Add..."
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '7px 10px',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e1',
                    fontSize: '12px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => handleAddOption()}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', height: 'auto' }}
                >
                  + Add
                </button>
              </div>

              {/* Options List */}
              <div
                style={{
                  maxHeight: '160px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                  padding: '4px 0',
                }}
              >
                {options.length === 0 ? (
                  <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic', padding: '8px' }}>
                    No options defined yet. Add choices using the field above.
                  </div>
                ) : (
                  options.map((opt, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px',
                      }}
                    >
                      <span style={{ fontWeight: opt.toLowerCase() === 'other' ? 700 : 500, color: opt.toLowerCase() === 'other' ? '#4f46e5' : '#1e293b' }}>
                        {idx + 1}. {opt} {opt.toLowerCase() === 'other' ? '(Triggers manual input box)' : ''}
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => handleMoveOption(idx, 'UP')}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: idx === 0 ? 'default' : 'pointer',
                            opacity: idx === 0 ? 0.3 : 1,
                            fontSize: '11px',
                            color: '#64748b',
                            padding: '2px 4px',
                          }}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          disabled={idx === options.length - 1}
                          onClick={() => handleMoveOption(idx, 'DOWN')}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: idx === options.length - 1 ? 'default' : 'pointer',
                            opacity: idx === options.length - 1 ? 0.3 : 1,
                            fontSize: '11px',
                            color: '#64748b',
                            padding: '2px 4px',
                          }}
                          title="Move down"
                        >
                          ▼
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(idx)}
                          style={{
                            background: '#fee2e2',
                            color: '#b91c1c',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 700,
                          }}
                          title="Delete option"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* 'Other' Helper Bar */}
              <div
                style={{
                  marginTop: '10px',
                  paddingTop: '8px',
                  borderTop: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#475569', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={hasOther}
                    onChange={handleToggleOther}
                  />
                  <span>Include <strong>"Other"</strong> option for free-text manual entry</span>
                </label>
                {hasOther && (
                  <span style={{ fontSize: '10px', color: '#059669', fontWeight: 600 }}>
                    ✓ Manual entry active
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Help Text / Placeholder */}
          <div className="form-field" style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
              Help Text / Guidance (Optional)
            </label>
            <input
              type="text"
              value={helpText}
              onChange={(e) => setHelpText(e.target.value)}
              placeholder="e.g. Select flight phase from list or choose Other to enter custom details."
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1' }}
            />
          </div>

          {/* Required Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
            <input
              type="checkbox"
              id="is_req_check"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
            />
            <label htmlFor="is_req_check" style={{ fontSize: '12px', fontWeight: 600, color: '#1e293b', cursor: 'pointer' }}>
              Mandatory field for reporter (*)
            </label>
          </div>

          {/* Footer Actions */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              paddingTop: '12px',
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              style={{ width: 'auto', padding: '8px 16px' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
              style={{ width: 'auto', padding: '8px 20px', minWidth: '120px' }}
            >
              {saving ? 'Saving...' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
